import type { ChartReviewResponse } from '@ctw/shared/api/ai/types';
import useConfig from '@ctw/shared/api/ai/use-config';
import { useSummariesForPatient } from '@ctw/shared/api/ai/use-precompute-summary';
import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { NoDataError } from '@ctw/shared/components/errors/no-data-error';
import { Heading } from '@ctw/shared/components/heading';
import SetTimeProgressBar from '@ctw/shared/components/indicators/set-time-progress-bar';
import { TwoColumnContent } from '@ctw/shared/components/layout/two-column-content';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { PrecomputedSummary } from '@ctw/shared/content/chart-review/summarization-v1/precomputed-summary';
import {
  type OverviewResources,
  PatientOverview,
} from '@ctw/shared/content/overview/patient-overview';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { siblingPathname } from '@ctw/shared/utils/url';
import { faSidebar } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export interface PatientSummaryProps {
  className?: ClassName;
}

/** Builds an absolute path for a resource sibling to the current patient tab and navigates there. */
export function useNavigateToOverviewResource(): (resource: OverviewResources) => void {
  const { navigate, pathname } = useZuiContext();
  return useCallback(
    (resource: OverviewResources) => {
      navigate(siblingPathname(pathname, resource));
    },
    [navigate, pathname],
  );
}

export const PatientSummary = withErrorBoundary({
  boundaryName: 'PatientSummary',
  Component: ({ className }: PatientSummaryProps) => {
    const { patient } = usePatientContext();
    const telemetry = useTelemetry();
    const { requestContext } = useCTW();

    const [displaySummaryType, setDisplaySummaryType] = useState<string | null>(null);

    const allowSummaryRegeneration = useMemo(
      () => requestContext.authTokenState.userType === 'zus',
      [requestContext],
    );
    const configs = useConfig(requestContext.builderId || '');

    // Set the summary type once configs are loaded
    useEffect(() => {
      if (configs.configs && displaySummaryType === null) {
        setDisplaySummaryType(configs.configs?.DefaultSummarySlug ?? 'general');
      }
    }, [configs.configs, displaySummaryType]);

    const [isLoading, setIsLoading] = useState(false);
    // Track if we're manually fetching to avoid duplicate requests
    const isFetchingRef = useRef(false);

    const {
      document: hookDocument,
      getSummaries,
      regenerateSummaries,
      isGenerating,
    } = useSummariesForPatient(displaySummaryType);

    const [localDocuments, setLocalDocuments] = useState<Array<ChartReviewResponse> | undefined>(
      undefined,
    );

    // Memoize document to prevent unnecessary re-renders
    const responses = useMemo(() => localDocuments || hookDocument, [localDocuments, hookDocument]);

    const containerRef = useRef<HTMLDivElement>(null);
    const { breakpoints } = useContainerQuery({ containerRef });

    const redirectTo = useNavigateToOverviewResource();

    const [isEvidenceSetToVisible, setIsEvidenceSetToVisible] = useState(true);

    const handleGetSummaries = useCallback(async () => {
      try {
        return await getSummaries(displaySummaryType);
      } catch (error) {
        return error;
      }
    }, [getSummaries, displaySummaryType]);

    const handleRegenerateSummaries = useCallback(async () => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const results = await regenerateSummaries(displaySummaryType);
        setLocalDocuments(results);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        telemetry.trackError({
          message: 'Error regenerating summary',
          error: err,
        });
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, [regenerateSummaries, displaySummaryType, telemetry]);

    // create array of PrecomputedSummary components
    const precomputedSummaries = useMemo(() => {
      // If actively regenerating (manual button click), show a single, global loading indicator.
      if (isLoading || !responses || responses.length === 0) {
        return (
          <SetTimeProgressBar
            totalTimeSeconds={30}
            stages={{
              0: 'Finding patient data',
              20: 'Grouping data by medical relevancy',
              40: 'Generating summary',
            }}
          />
        );
      }

      // If not manually loading (isLoading is false):
      // Handle initial loading state or if the summary type isn't set yet,
      // and there are no responses yet.
      if ((!responses || responses.length === 0) && (isGenerating || !displaySummaryType)) {
        return (
          <SetTimeProgressBar
            totalTimeSeconds={30}
            stages={{
              0: 'Finding patient data',
              20: 'Grouping data by medical relevancy',
              40: 'Generating summary',
            }}
          />
        );
      }

      // If all loading is complete (isLoading is false, isGenerating is false)
      // and there are still no responses, the parent component will show NoDataError.
      // So, return null here to avoid rendering anything extra from this memoized value.
      if (!responses || responses.length === 0) {
        return null;
      }

      // If not loading and responses are available, map them.
      return responses.map((doc, idx) => (
        <PrecomputedSummary
          // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
          key={`summary-${doc.title ?? 'generated'}-${idx}`}
          onGenerateSummary={handleGetSummaries}
          document={doc}
          isGenerating={isGenerating || !displaySummaryType} // isLoading is handled globally above for regeneration
          className={twx(
            'rounded-lg border border-background-subtle border-rounded-lg p-4',
            className,
          )}
        />
      ));
    }, [responses, handleGetSummaries, isGenerating, isLoading, displaySummaryType, className]);

    return (
      <div ref={containerRef} className={tw`relative flex h-full flex-col px-2 pt-2`}>
        <Heading level="h1" className={tw`font-normal font-size-3xl`}>
          Your 5-Minute Briefing on {patient.displayFull}
        </Heading>
        {responses?.length === 0 ? (
          <NoDataError
            className={tw`my-4 rounded-lg bg-background-hover`}
            message="Sorry, we weren't able to generate a summary for this patient."
          />
        ) : (
          <TwoColumnContent
            leftColumn={{
              hidden: !(breakpoints.isAtLeast.md && isEvidenceSetToVisible),
              header: (
                <div className={tw`flex flex-1 items-center gap-2`}>
                  {breakpoints.isAtLeast.md && (
                    <button
                      type="button"
                      onClick={() => {
                        telemetry.trackInteraction('patient_summary_evidence_drawer_close');
                        setIsEvidenceSetToVisible(false);
                      }}
                      className={tw`flex h-4 w-4 items-center justify-center`}
                      aria-label="Close Evidence Drawer"
                    >
                      <FontAwesomeIcon
                        icon={faSidebar}
                        className={tw`text-content-icon hover:text-content-subtle`}
                      />
                    </button>
                  )}
                  <Heading
                    level="h2"
                    className={twx(
                      'line-clamp-2 flex items-center gap-2 text-content-subtle text-xs uppercase',
                    )}
                  >
                    Clinical Evidence
                  </Heading>
                </div>
              ),
              content: (
                <PatientOverview onSeeAllResources={redirectTo} className={tw`min-w-[320px]`} />
              ),
            }}
            rightColumn={{
              header: (
                <div className={tw`flex min-w-0 flex-1 justify-between gap-2`}>
                  <div className={tw`flex items-center gap-2`}>
                    {!isEvidenceSetToVisible && breakpoints.isAtLeast.md && (
                      <button
                        type="button"
                        onClick={() => {
                          telemetry.trackInteraction('patient_summary_evidence_drawer_open');
                          setIsEvidenceSetToVisible(true);
                        }}
                        className={tw`flex h-4 w-4 items-center justify-center`}
                      >
                        <FontAwesomeIcon
                          icon={faSidebar}
                          className={tw`text-content-icon hover:text-content-subtle`}
                        />
                      </button>
                    )}
                    <Heading
                      level="h2"
                      className={twx(
                        'line-clamp-2 flex w-fit items-center gap-2 text-content-subtle text-xs uppercase',
                      )}
                    >
                      Summary of all available information
                    </Heading>
                    <Tooltip
                      id="patient-summary-generated-time"
                      content={`Generated ${formatDateToLocalString(responses?.[0]?.createdAt)}`}
                    >
                      <FontAwesomeIcon
                        className={tw`block h-4 w-4 text-content-icon`}
                        icon={faInfoCircle}
                      />
                    </Tooltip>
                  </div>
                  <div className={twx('flex items-center gap-1')}>
                    {allowSummaryRegeneration && (
                      <Button
                        variant="unstyled"
                        size="sm"
                        type="button"
                        ariaLabel="Regenerate Summary"
                        onClick={handleRegenerateSummaries}
                      >
                        <FontAwesomeIcon icon={faRefresh} className={twx('text-content-icon')} />
                      </Button>
                    )}
                  </div>
                </div>
              ),
              content: (
                <div className={twx('space-y-4')}>
                  {precomputedSummaries}
                  {Array.isArray(responses) && responses.length > 0 && (
                    <div className={tw`pt-2 text-xs italic`}>
                      This health record summary is generated using AI technology and is intended
                      for informational purposes only. While efforts have been made to ensure
                      accuracy, the information provided may not capture all relevant details or
                      updates.
                    </div>
                  )}
                </div>
              ),
            }}
          />
        )}
      </div>
    );
  },
});
